import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    FormControl,
    FormLabel,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

export default function TrainModelDialog({ open, onClose, onSubmit, trainingSet }) {
    const [modelType, setModelType] = useState("new"); // "new" or "existing"
    const [modelName, setModelName] = useState("");
    // For existing models, store the selected model's name directly.
    const [selectedModelName, setSelectedModelName] = useState("");
    const [schedule, setSchedule] = useState("now"); // "now" or "overnight"
    const [existingModels, setExistingModels] = useState([]);

    useEffect(() => {
        if (modelType === "existing") {
            async function fetchExistingModels() {
                try {
                    const response = await fetch('/api/DocumentModels', {
                        method: 'GET',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        // Use the returned array from data.models (all lowercase)
                        const models = data?.models ?? [];
                        setExistingModels(models);
                    } else {
                        console.error("Error fetching existing models");
                        setExistingModels([]);
                    }
                } catch (error) {
                    console.error("Error fetching existing models:", error);
                    setExistingModels([]);
                }
            }
            fetchExistingModels();
        }
    }, [modelType]);

    const handleSubmit = () => {
        if (modelType === "new" && !modelName.trim()) {
            alert("Please provide a model name for the new model.");
            return;
        }
        if (modelType === "existing" && !selectedModelName) {
            alert("Please select an existing model.");
            return;
        }
        const effectiveModelName = modelType === "new" ? modelName : selectedModelName;
        if (!effectiveModelName) {
            alert("Selected model is invalid.");
            return;
        }
        const payload = {
            trainingSetId: trainingSet?.trainingSetId,
            isNewModel: modelType === "new",
            modelName: effectiveModelName,
            trainImmediately: schedule === "now"
        };
        console.log("Submitting payload:", payload);
        onSubmit(payload);
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Train Model for Training Set: {trainingSet?.name}</DialogTitle>
            <DialogContent dividers>
                <FormControl component="fieldset" margin="normal" fullWidth>
                    <FormLabel component="legend">Model Type</FormLabel>
                    <RadioGroup
                        row
                        value={modelType}
                        onChange={(e) => {
                            setModelType(e.target.value);
                            // Clear previous values when switching.
                            setModelName("");
                            setSelectedModelName("");
                        }}
                    >
                        <FormControlLabel value="new" control={<Radio />} label="New Model" />
                        <FormControlLabel value="existing" control={<Radio />} label="Existing Model" />
                    </RadioGroup>
                </FormControl>
                {modelType === "new" ? (
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Model Name"
                        fullWidth
                        variant="outlined"
                        value={modelName}
                        onChange={(e) => setModelName(e.target.value)}
                    />
                ) : (
                    <FormControl fullWidth margin="normal">
                        <FormLabel>Choose Existing Model</FormLabel>
                        <Autocomplete
                            options={existingModels}
                            getOptionLabel={(option) => option.name}
                            value={
                                existingModels.find((m) => m.name === selectedModelName) || null
                            }
                            onChange={(event, newValue) =>
                                setSelectedModelName(newValue ? newValue.name : "")
                            }
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Select Model" variant="outlined" />
                            )}
                            clearOnEscape
                        />
                    </FormControl>
                )}
                <FormControl component="fieldset" margin="normal" fullWidth>
                    <FormLabel component="legend">Schedule</FormLabel>
                    <RadioGroup
                        row
                        value={schedule}
                        onChange={(e) => setSchedule(e.target.value)}
                    >
                        <FormControlLabel value="now" control={<Radio />} label="Train Now" />
                        <FormControlLabel value="overnight" control={<Radio />} label="Train Overnight" />
                    </RadioGroup>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}
